import React, { useState } from 'react';
import {
  Fab, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Box, Typography, Radio, RadioGroup, FormControlLabel,
  Alert, IconButton, Snackbar
} from '@mui/material';
import FeedbackIcon from '@mui/icons-material/Feedback';
import CloseIcon from '@mui/icons-material/Close';
import api from '../services/api';

export default function FeedbackButton() {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState('bug');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [screenshot, setScreenshot] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [snack, setSnack] = useState({ open: false, msg: '' });

  const reset = () => {
    setCategory('bug');
    setSubject('');
    setMessage('');
    setScreenshot(null);
    setError('');
  };

  const handleClose = () => {
    if (submitting) return;
    setOpen(false);
    setTimeout(reset, 300);
  };

  const handleSubmit = async () => {
    if (subject.trim().length < 3) { setError('Subject must be at least 3 characters'); return; }
    if (message.trim().length < 5) { setError('Message must be at least 5 characters'); return; }
    setError('');
    setSubmitting(true);
    try {
      let screenshotUrl = null;
      if (screenshot) {
        // 1. Get signed URL
        const sig = await api.post('/feedback/screenshot-upload-url', {
          filename: screenshot.name,
          content_type: screenshot.type || 'image/png',
        });
        // 2. Upload directly to GCS
        const up = await fetch(sig.data.signed_url, {
          method: 'PUT',
          headers: { 'Content-Type': sig.data.content_type },
          body: screenshot,
        });
        if (!up.ok) throw new Error('Screenshot upload failed');
        screenshotUrl = sig.data.public_url;
      }
      await api.post('/feedback', {
        category,
        subject: subject.trim(),
        message: message.trim(),
        screenshot_url: screenshotUrl,
        page_path: window.location.pathname,
        user_agent: navigator.userAgent,
      });
      setOpen(false);
      setTimeout(reset, 300);
      setSnack({ open: true, msg: 'Thanks — we got your feedback!' });
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to send');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Fab
        color="primary"
        aria-label="feedback"
        onClick={() => setOpen(true)}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          bgcolor: '#1a237e',
          '&:hover': { bgcolor: '#283593' },
          zIndex: 1200,
        }}
      >
        <FeedbackIcon />
      </Fab>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 800, color: '#1a237e', borderBottom: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Send Feedback
          <IconButton onClick={handleClose} size="small" disabled={submitting}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body2" sx={{ mb: 2, color: '#555' }}>
            Help us improve Yaplar. Tell us what's broken, what you need, or what's confusing.
          </Typography>

          <Typography variant="caption" sx={{ fontWeight: 700, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1 }}>Category</Typography>
          <RadioGroup row value={category} onChange={e => setCategory(e.target.value)} sx={{ mb: 2 }}>
            <FormControlLabel value="bug" control={<Radio size="small" />} label="🐛 Bug" />
            <FormControlLabel value="feature" control={<Radio size="small" />} label="✨ Feature" />
            <FormControlLabel value="question" control={<Radio size="small" />} label="❓ Question" />
          </RadioGroup>

          <TextField
            label="Subject"
            value={subject}
            onChange={e => setSubject(e.target.value)}
            fullWidth size="small" sx={{ mb: 2 }}
            placeholder="One-line summary"
            inputProps={{ maxLength: 200 }}
          />

          <TextField
            label="Tell us more"
            value={message}
            onChange={e => setMessage(e.target.value)}
            fullWidth multiline rows={5} sx={{ mb: 2 }}
            placeholder={category === 'bug' ? 'What did you expect to happen, and what actually happened? Steps to reproduce help a lot.' : (category === 'feature' ? 'What would you like to be able to do? Why?' : 'What would you like to know?')}
            inputProps={{ maxLength: 5000 }}
          />

          <Box sx={{ mb: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>Screenshot (optional)</Typography>
            <input
              type="file"
              accept="image/*"
              onChange={e => setScreenshot(e.target.files?.[0] || null)}
              style={{ fontSize: 13 }}
            />
            {screenshot && (
              <Typography variant="caption" sx={{ display: 'block', color: '#2e7d32', mt: 0.5 }}>
                {screenshot.name} · {(screenshot.size / 1024).toFixed(0)} KB
              </Typography>
            )}
          </Box>

          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #e0e0e0', gap: 1 }}>
          <Button onClick={handleClose} disabled={submitting}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting || !subject.trim() || !message.trim()}
            sx={{ bgcolor: '#1a237e', borderRadius: 2, px: 3 }}
          >
            {submitting ? 'Sending…' : 'Send Feedback'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack({ open: false, msg: '' })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSnack({ open: false, msg: '' })}>{snack.msg}</Alert>
      </Snackbar>
    </>
  );
}
